"use client";

import { createContext, useContext, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";

type ToastType = "success" | "error" | "warning" | "info";

interface ToastData {
  id: number;
  message: string;
  type: ToastType;
}

interface ToastContextValue {
  showToast: (message: string, type?: ToastType) => void;
}

const NeuralToastContext = createContext<ToastContextValue | undefined>(
  undefined
);

export function NeuralToastProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [toasts, setToasts] = useState<ToastData[]>([]);

  const showToast = useCallback((message: string, type: ToastType = "info") => {
    const id = Date.now();
    setToasts((prev) => [...prev, { id, message, type }]);
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 3500);
  }, []);

  return (
    <NeuralToastContext.Provider value={{ showToast }}>
      {children}
      {/* Single toast layer at root */}
      <div className="fixed bottom-8 right-8 z-[9999] flex flex-col gap-2 items-end">
        <AnimatePresence>
          {toasts.map((toast) => (
            <motion.div
              key={toast.id}
              initial={{ y: 60, opacity: 0, scale: 0.9 }}
              animate={{ y: 0, opacity: 1, scale: 1 }}
              exit={{ y: 40, opacity: 0 }}
              transition={{ type: "spring", stiffness: 200, damping: 18 }}
              className={`px-6 py-3 font-rajdhani uppercase tracking-wider text-sm select-none pointer-events-none backdrop-blur-md border ${getStyleForType(
                toast.type
              )}`}
            >
              {getIcon(toast.type)} {toast.message}
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </NeuralToastContext.Provider>
  );
}

export function useNeuralToast() {
  const ctx = useContext(NeuralToastContext);
  if (!ctx)
    throw new Error("useNeuralToast must be used within NeuralToastProvider");
  return ctx;
}

// 🔮 Style helpers
function getStyleForType(type: ToastType) {
  switch (type) {
    case "success":
      return "text-green-200 border-green-400/40 bg-green-900/30 shadow-[0_0_12px_#00ff9960]";
    case "error":
      return "text-pink-200 border-pink-500/40 bg-pink-900/30 shadow-[0_0_12px_#ff009980]";
    case "warning":
      return "text-amber-200 border-amber-400/40 bg-amber-900/30 shadow-[0_0_12px_#ffcc0080]";
    case "info":
    default:
      return "text-cyan-200 border-cyan-400/40 bg-cyan-900/30 shadow-[0_0_12px_#00ffff80]";
  }
}

function getIcon(type: ToastType) {
  switch (type) {
    case "success":
      return "✔";
    case "error":
      return "✖";
    case "warning":
      return "⚠";
    case "info":
    default:
      return "ℹ";
  }
}
